/**
 * chores.ts — chores + habit wishlists + reward requests + tasks
 * (PR-15 split of api/client.ts).
 *
 * Owns the "things the kid does for points/permissions" endpoints
 * outside the daily-rituals system. Chores come from the parent admin,
 * wishlists are kid-authored, reward requests are kid-requested
 * payouts pending parent approval, tasks are parent-set one-off jobs.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by HabitsPage (chores + wishlists + reward requests),
 *     ScheduleStart flows (claimChore), and the side-rail "tasks" row.
 *   - Backend: backend/src/routes/chores.js, wishlists.js,
 *     reward_requests.js, tasks.js.
 *
 * 关键约束：
 *   - claimChore returns auto_approved=false when the parent has the
 *     chore set to manual review; UI must show a "pending" state instead
 *     of crediting coins client-side.
 */
import type { Chore, HabitWishlist, RewardRequest, Task } from '@xiaopeiban/shared';
import { call } from './_core';

export const choresApi = {
  // Chores & Tasks
  listChores() {
    return call<{ chores: Chore[] }>('/api/v1/child/chores');
  },
  claimChore(choreId: number, note?: string) {
    return call<{ ok: true; auto_approved: boolean; awarded: number }>(
      `/api/v1/child/chores/${choreId}/claim`,
      { method: 'POST', body: JSON.stringify({ note }) },
    );
  },
  listWishlists() {
    return call<{ wishlists: HabitWishlist[] }>('/api/v1/child/wishlists');
  },
  listRewardRequests() {
    return call<{ requests: RewardRequest[] }>('/api/v1/child/reward-requests');
  },
  createRewardRequest(title: string, amount_requested: number, description?: string) {
    return call<{ ok: true; id: number }>('/api/v1/child/reward-requests', {
      method: 'POST',
      body: JSON.stringify({ title, description, amount_requested }),
    });
  },
  listTasks() {
    return call<{ tasks: Task[] }>('/api/v1/child/tasks');
  },
  submitTask(taskId: number, note?: string) {
    return call<{ ok: true }>(`/api/v1/child/tasks/${taskId}/submit`, {
      method: 'POST', body: JSON.stringify({ note }),
    });
  },
};
